(() => {
  if (window.__kohreeCouponDefined) return;
  window.__kohreeCouponDefined = true;

  const TRUE_VALUES = new Set(['true', 'yes', '1', 'on']);
  const COUNTDOWN_STORAGE_VERSION = 'v3-cycle';
  const LEGACY_COUNTDOWN_STORAGE_VERSION = 'v2';
  const COUNTDOWN_CYCLE_MS = 24 * 60 * 60 * 1000;

  class KohreeProductCoupon extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.dataEl = this.querySelector('[data-coupon-variant-data]');
      if (!this.dataEl) return;

      try {
        this.couponData = JSON.parse(this.dataEl.textContent);
      } catch (error) {
        console.warn('[KOHREE coupon] Invalid variant data.', error);
        return;
      }

      this.productId = String(this.dataset.productId || '');
      this.sectionId = String(this.dataset.sectionId || '');
      this.initialVariantId = String(this.dataset.initialVariantId || '');
      this.discountEl = this.querySelector('[data-coupon-discount]');
      this.copyDiscountEl = this.querySelector('[data-coupon-copy-discount]');
      this.copyButton = this.querySelector('[data-coupon-copy]');
      this.countdownRow = this.querySelector('[data-coupon-countdown-row]');
      this.countdownText = this.querySelector('[data-coupon-countdown-text]');
      this.infoOpen = this.querySelector('[data-coupon-info-open]');
      this.modal = this.querySelector('[data-coupon-modal]');
      this.modalPanel = this.modal?.querySelector('.kohree-coupon-modal__panel') || null;
      this.modalHeader = this.modal?.querySelector('.kohree-coupon-modal__header') || null;
      this.modalBody = this.modal?.querySelector('.kohree-coupon-modal__body') || null;
      this.modalHome = this.modal?.parentNode || null;
      this.modalHomeNextSibling = this.modal?.nextSibling || null;
      this.timer = null;
      this.copyResetTimer = null;
      this.modalCloseTimer = null;
      this.lastFocusedElement = null;
      this.scrollLockState = null;
      this.modalIsOpen = false;

      this.onCopy = this.onCopy.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onProductPageVariantUpdated = this.onProductPageVariantUpdated.bind(this);
      this.onModalOpen = this.openModal.bind(this);
      this.onModalClose = this.closeModal.bind(this);
      this.onModalKeydown = this.onModalKeydown.bind(this);
      this.syncModalSize = this.syncModalSize.bind(this);

      if (this.copyButton) this.copyButton.addEventListener('click', this.onCopy);
      document.addEventListener('eurus:variant:change', this.onVariantChange);
      document.addEventListener(`eurus:product-page-variant-select:updated:${this.sectionId}`, this.onProductPageVariantUpdated);

      if (this.infoOpen && this.modal) {
        this.infoOpen.addEventListener('click', this.onModalOpen);

        // IMPORTANT: only the real X button closes the modal.
        // The transparent outside area is intentionally non-closing so text
        // selection, touch scrolling and mouse dragging cannot be mistaken for close actions.
        this.modal.querySelectorAll('.kohree-coupon-modal__close[data-coupon-modal-close]').forEach((button) => {
          button.addEventListener('click', this.onModalClose);
        });
        this.modal.addEventListener('keydown', this.onModalKeydown);
      }

      this.render(this.resolveVariantId());
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      window.clearTimeout(this.copyResetTimer);
      window.clearTimeout(this.modalCloseTimer);
      if (this.copyButton) this.copyButton.removeEventListener('click', this.onCopy);
      document.removeEventListener('eurus:variant:change', this.onVariantChange);
      document.removeEventListener(`eurus:product-page-variant-select:updated:${this.sectionId}`, this.onProductPageVariantUpdated);
      if (this.infoOpen) this.infoOpen.removeEventListener('click', this.onModalOpen);
      if (this.modal) this.modal.removeEventListener('keydown', this.onModalKeydown);
      window.removeEventListener('resize', this.syncModalSize);
      window.visualViewport?.removeEventListener('resize', this.syncModalSize);

      if (this.modalIsOpen) this.unlockPageScroll();
      if (this.modal && this.modal.parentNode === document.body) this.modal.remove();
    }

    normalizeFlag(value) {
      if (value === true) return true;
      return TRUE_VALUES.has(String(value == null ? '' : value).trim().toLowerCase());
    }

    resolveVariantId(explicitVariantId) {
      const explicit = String(explicitVariantId || '');
      if (explicit && this.couponData.variants?.[explicit]) return explicit;

      const input = document.getElementById(`update-variant-${this.sectionId}`);
      const inputValue = String(input?.value || '');
      if (inputValue && this.couponData.variants?.[inputValue]) return inputValue;

      return this.initialVariantId;
    }

    getVariantData(variantId) {
      const defaults = this.couponData.productDefaults || {};
      const variant = this.couponData.variants?.[String(variantId)] || {};

      return {
        discount: variant.discount || defaults.discount || '',
        discountCode: variant.discount_code || defaults.discount_code || '',
        deadlineTs: Number(variant.discount_deadline_ts || defaults.discount_deadline_ts || 0),
        use24hr: this.normalizeFlag(
          variant.discount_24hr_countdown === null || variant.discount_24hr_countdown === ''
            ? defaults.discount_24hr_countdown
            : variant.discount_24hr_countdown
        )
      };
    }

    get24hrCampaignFingerprint(item) {
      return [
        String(item?.discount || '').trim(),
        String(item?.discountCode || '').trim(),
        String(item?.deadlineTs || 0)
      ].join('|');
    }

    get24hrStorageKey(variantId, version = COUNTDOWN_STORAGE_VERSION) {
      return `kohree-coupon-24hr:${version}:${this.productId}:${variantId}`;
    }

    read24hrRecord(storageKey) {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return null;

      try {
        const saved = JSON.parse(raw);
        return {
          endAt: Number(saved?.endAt || 0),
          fingerprint: String(saved?.fingerprint || '')
        };
      } catch (error) {
        return null;
      }
    }

    write24hrRecord(storageKey, endAt, fingerprint) {
      window.localStorage.setItem(storageKey, JSON.stringify({
        endAt,
        fingerprint
      }));
    }

    getSession24hrEnd(variantId, fingerprint, forceNewCycle = false) {
      const now = Date.now();
      const sessionKey = `${variantId}|${fingerprint}`;

      if (!this._session24hrTimers) this._session24hrTimers = new Map();
      const savedEndAt = Number(this._session24hrTimers.get(sessionKey) || 0);

      if (!forceNewCycle && savedEndAt > now) return savedEndAt;

      const endAt = now + COUNTDOWN_CYCLE_MS;
      this._session24hrTimers.set(sessionKey, endAt);
      return endAt;
    }

    getPersistent24hrEnd(variantId, item, forceNewCycle = false) {
      const storageKey = this.get24hrStorageKey(variantId);
      const legacyStorageKey = this.get24hrStorageKey(variantId, LEGACY_COUNTDOWN_STORAGE_VERSION);
      const fingerprint = this.get24hrCampaignFingerprint(item);
      const now = Date.now();

      try {
        const saved = this.read24hrRecord(storageKey);

        // Same campaign + still running: keep exactly the same local end time.
        // Refreshing/reopening the page never restarts the 24-hour timer.
        if (
          !forceNewCycle &&
          saved?.fingerprint === fingerprint &&
          saved.endAt > now
        ) {
          return saved.endAt;
        }

        // One-time migration from v2. If the old timer is still active, preserve
        // its remaining time so installing this update does not reset visitors.
        if (!saved && !forceNewCycle) {
          const legacy = this.read24hrRecord(legacyStorageKey);
          if (legacy?.fingerprint === fingerprint && legacy.endAt > now) {
            this.write24hrRecord(storageKey, legacy.endAt, fingerprint);
            return legacy.endAt;
          }
        }

        // We only arrive here when one of these is true:
        // 1) the previous 24-hour cycle has actually expired;
        // 2) JS explicitly asks for the next cycle after reaching 00:00:00;
        // 3) discount/code/deadline changed, which means a new campaign.
        const endAt = now + COUNTDOWN_CYCLE_MS;
        this.write24hrRecord(storageKey, endAt, fingerprint);
        return endAt;
      } catch (error) {
        // localStorage can be blocked in strict privacy modes. In that case the
        // timer remains stable for the current page session, but cannot survive
        // a full browser refresh because the browser denied persistent storage.
        return this.getSession24hrEnd(variantId, fingerprint, forceNewCycle);
      }
    }

    restartPersistent24hrCycle(variantId, item) {
      return this.getPersistent24hrEnd(variantId, item, true);
    }

    pad(value) {
      return String(value).padStart(2, '0');
    }

    formatCountdown(remainingMs) {
      const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (days > 0) {
        return `Ends in ${days} Day${days === 1 ? '' : 's'} ${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`;
      }

      return `Ends in ${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`;
    }

    showCoupon() {
      this.classList.remove('kohree-coupon--hidden');
    }

    hideCoupon() {
      this.classList.add('kohree-coupon--hidden');
      window.clearInterval(this.timer);
    }

    updateCopyButton(code) {
      if (!this.copyButton) return;
      this.copyButton.dataset.code = code || '';
      this.copyButton.disabled = !code;
      this.copyButton.textContent = code ? 'Copy Code' : 'Applied at checkout';
    }

    render(variantId) {
      window.clearInterval(this.timer);
      const resolvedVariantId = this.resolveVariantId(variantId);
      const item = this.getVariantData(resolvedVariantId);

      if (!item.discount) {
        this.hideCoupon();
        return;
      }

      let endAt = item.use24hr
        ? this.getPersistent24hrEnd(resolvedVariantId, item)
        : item.deadlineTs * 1000;

      if (!endAt) {
        this.hideCoupon();
        return;
      }

      if (!item.use24hr && Date.now() >= endAt) {
        this.hideCoupon();
        return;
      }

      this.showCoupon();
      if (this.discountEl) this.discountEl.textContent = item.discount;
      if (this.copyDiscountEl) this.copyDiscountEl.textContent = item.discount;
      this.updateCopyButton(item.discountCode);

      const updateCountdown = () => {
        let remaining = endAt - Date.now();

        if (remaining <= 0) {
          if (item.use24hr) {
            // The current local 24-hour cycle has genuinely finished.
            // Start the next 24-hour cycle immediately without hiding/flickering
            // the coupon. A page refresh before this point never resets it.
            endAt = this.restartPersistent24hrCycle(resolvedVariantId, item);
            remaining = endAt - Date.now();
            this.showCoupon();
          } else {
            this.hideCoupon();
            return;
          }
        }

        if (this.countdownRow) this.countdownRow.hidden = false;
        if (this.countdownText) this.countdownText.textContent = this.formatCountdown(remaining);
      };

      updateCountdown();
      this.timer = window.setInterval(updateCountdown, 1000);
    }

    onVariantChange(event) {
      const variantId = event.detail?.variant?.id;
      if (!variantId || !this.couponData.variants?.[String(variantId)]) return;
      this.render(String(variantId));
    }

    onProductPageVariantUpdated() {
      window.requestAnimationFrame(() => this.render(this.resolveVariantId()));
    }

    async copyText(text) {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }

      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      textarea.remove();
      if (!copied) throw new Error('Copy failed');
    }

    async onCopy() {
      const code = this.copyButton?.dataset.code || '';
      if (!code) return;

      window.clearTimeout(this.copyResetTimer);
      try {
        await this.copyText(code);
        this.copyButton.textContent = 'Copied!';
      } catch (error) {
        this.copyButton.textContent = 'Copy failed';
      }

      this.copyResetTimer = window.setTimeout(() => {
        if (this.copyButton?.dataset.code) this.copyButton.textContent = 'Copy Code';
      }, 1600);
    }

    portalModalToBody() {
      if (!this.modal || this.modal.parentNode === document.body) return;
      document.body.appendChild(this.modal);
    }

    restoreModalHome() {
      if (!this.modal || !this.modalHome || !this.modalHome.isConnected) return;
      if (this.modalHomeNextSibling && this.modalHomeNextSibling.parentNode === this.modalHome) {
        this.modalHome.insertBefore(this.modal, this.modalHomeNextSibling);
      } else {
        this.modalHome.appendChild(this.modal);
      }
    }

    lockPageScroll() {
      if (this.scrollLockState) return;

      const html = document.documentElement;
      const body = document.body;
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const scrollbarWidth = Math.max(0, window.innerWidth - html.clientWidth);

      this.scrollLockState = {
        scrollY,
        htmlOverflow: html.style.overflow,
        htmlOverscrollBehavior: html.style.overscrollBehavior,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyLeft: body.style.left,
        bodyRight: body.style.right,
        bodyWidth: body.style.width,
        bodyOverflow: body.style.overflow,
        bodyPaddingRight: body.style.paddingRight
      };

      html.style.overflow = 'hidden';
      html.style.overscrollBehavior = 'none';
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
    }

    unlockPageScroll() {
      if (!this.scrollLockState) return;

      const html = document.documentElement;
      const body = document.body;
      const state = this.scrollLockState;

      html.style.overflow = state.htmlOverflow;
      html.style.overscrollBehavior = state.htmlOverscrollBehavior;
      body.style.position = state.bodyPosition;
      body.style.top = state.bodyTop;
      body.style.left = state.bodyLeft;
      body.style.right = state.bodyRight;
      body.style.width = state.bodyWidth;
      body.style.overflow = state.bodyOverflow;
      body.style.paddingRight = state.bodyPaddingRight;

      this.scrollLockState = null;
      window.scrollTo(0, state.scrollY);
    }

    syncModalSize() {
      if (!this.modalIsOpen || !this.modal || !this.modalBody || !this.modalHeader || !this.modalPanel) return;

      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const modalStyles = window.getComputedStyle(this.modal);
      const paddingTop = Number.parseFloat(modalStyles.paddingTop) || 0;
      const paddingBottom = Number.parseFloat(modalStyles.paddingBottom) || 0;
      const panelAvailable = Math.max(240, viewportHeight - paddingTop - paddingBottom);
      const headerHeight = this.modalHeader.getBoundingClientRect().height;
      const bodyAvailable = Math.max(140, panelAvailable - headerHeight);

      this.modalPanel.style.maxHeight = `${panelAvailable}px`;
      this.modalBody.style.maxHeight = `${bodyAvailable}px`;
    }

    openModal() {
      if (!this.modal || this.modalIsOpen) return;

      window.clearTimeout(this.modalCloseTimer);
      this.lastFocusedElement = document.activeElement;
      this.portalModalToBody();
      this.lockPageScroll();
      this.modalIsOpen = true;
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');

      window.addEventListener('resize', this.syncModalSize, { passive: true });
      window.visualViewport?.addEventListener('resize', this.syncModalSize, { passive: true });

      window.requestAnimationFrame(() => {
        this.syncModalSize();
        this.modal.classList.add('is-open');
        this.modal.querySelector('.kohree-coupon-modal__close')?.focus({ preventScroll: true });
      });
    }

    closeModal() {
      if (!this.modal || !this.modalIsOpen) return;

      this.modalIsOpen = false;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      window.removeEventListener('resize', this.syncModalSize);
      window.visualViewport?.removeEventListener('resize', this.syncModalSize);

      this.unlockPageScroll();

      this.modalCloseTimer = window.setTimeout(() => {
        this.modal.hidden = true;
        if (this.modalBody) this.modalBody.style.maxHeight = '';
        if (this.modalPanel) this.modalPanel.style.maxHeight = '';
        this.restoreModalHome();
      }, 230);

      this.lastFocusedElement?.focus?.({ preventScroll: true });
    }

    onModalKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        this.modal.querySelectorAll('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.disabled && !element.hidden);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('kohree-product-coupon')) {
    customElements.define('kohree-product-coupon', KohreeProductCoupon);
  }
})();
